import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

/**
 * Picks the product a listing should point at.
 *
 * Shared by the two halves of link management, which ask the same question from
 * opposite ends: the queue has a listing with no product, and the audit has one
 * pointing at the wrong product. Both need the whole browsable candidate list,
 * scoped to the right storefront, with the same search grammar — so this lives
 * in one place rather than being written twice and drifting.
 */
export function ProductPicker({
  listingId,
  actionLabel,
  busy,
  onChoose,
}: {
  listingId: string;
  /** "Link" when there is nothing there yet, "Use this" when re-pointing. */
  actionLabel: string;
  busy: boolean;
  onChoose: (productId: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [site, setSite] = useState<'auto' | 'all'>('auto');

  // No minimum length: opening the picker shows the whole candidate list, and
  // typing narrows it. Starting from a blank panel meant guessing a word that
  // might match before seeing anything at all.
  const results = useQuery({
    queryKey: ['product-search', query, listingId, site],
    queryFn: () => api.searchProducts(query, listingId, site),
  });

  const scopeName = results.data?.scope === 'mlf' ? 'Log Furniture' : 'Farmhouse';

  return (
    <div className="product-picker">
      <input
        autoFocus
        style={{ width: '100%' }}
        placeholder="Narrow by SKU or title — try: bed queen OR king -bunk"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      <div className="small muted" style={{ marginTop: 4 }}>
        Terms narrow together; <code>OR</code> offers alternatives; <code>-word</code> excludes.
        {results.data && (
          <>
            {' '}
            Showing {results.data.products.length} of {results.data.total}
            {results.data.scope && (
              <>
                {' '}in <strong>{scopeName}</strong>
                {/* An inferred scope is a guess, so it says so and offers a way
                    out; a channel-derived one is simply true. */}
                {results.data.scopeSource === 'title' && ' (guessed from the title)'}
                {' · '}
                <button className="link small" onClick={() => setSite('all')}>
                  search all products
                </button>
              </>
            )}
            {site === 'all' && (
              <>
                {' · '}
                <button className="link small" onClick={() => setSite('auto')}>
                  narrow again
                </button>
              </>
            )}
            .
          </>
        )}
      </div>

      {results.isLoading && <p className="small muted">Loading products…</p>}

      {results.data && results.data.products.length > 0 && (
        <ul className="link-candidates">
          {results.data.products.map((product) => (
            <li key={product.id}>
              <button className="link" disabled={busy} onClick={() => onChoose(product.id)}>
                {actionLabel}
              </button>{' '}
              <span className="sku">{product.skuDisplay}</span> — {product.title}
            </li>
          ))}
        </ul>
      )}

      {results.data?.products.length === 0 && (
        <p className="small muted">
          Nothing matches{results.data.scope ? ` in ${scopeName}` : ''}.
        </p>
      )}
    </div>
  );
}
